import { useMemo } from "react";
import { useDashboardData } from "../context/DashboardDataContext";
import { formatPct, PATTERNS, PATTERN_LABELS } from "../dashboard/analytics";
import { Card } from "../components/ui/Card";
import { KpiCard } from "../components/ui/KpiCard";
import { PageHeader } from "../components/ui/PageHeader";
import { HorizontalBars } from "../components/charts/HorizontalBars";

export function ConversionRevenue() {
  const { analytics } = useDashboardData();

  const byPattern = useMemo(
    () =>
      Object.entries(analytics.conversionByPattern)
        .map(([pattern, [conv, tot]]) => ({
          label: PATTERN_LABELS[pattern] || pattern,
          value: tot ? Math.round((conv / tot) * 100) : 0,
          display: formatPct(tot ? conv / tot : 0),
        }))
        .sort((a, b) => b.value - a.value),
    [analytics.conversionByPattern]
  );

  const byScore = useMemo(
    () =>
      Object.entries(analytics.conversionByScoreBand).map(([band, [conv, tot]]) => ({
        label: `Overall ${band}%`,
        value: tot ? Math.round((conv / tot) * 100) : 0,
        display: formatPct(tot ? conv / tot : 0),
      })),
    [analytics.conversionByScoreBand]
  );

  return (
    <div className="page">
      <PageHeader
        eyebrow="Conversion & revenue"
        title="Engagement to outcome"
        description="Report consumption, CTA engagement, and paid conversion inferred from telemetry."
      />

      <div className="kpi-strip">
        <KpiCard label="Report view rate" value={formatPct(analytics.reportViewedRate)} />
        <KpiCard label="CTA CTR" value={formatPct(analytics.ctaRate)} />
        <KpiCard
          label="Report → CTA (24h)"
          value={formatPct(analytics.reportToBookingIntentRate)}
          hint="Intent proxy"
        />
        <KpiCard label="Report → paid" value={formatPct(analytics.reportToPaidRate)} />
      </div>

      <div className="grid-2">
        <Card title="Conversion by primary pattern" subtitle="CTA or paid signal / assessments with that primary">
          {byPattern.length ? (
            <HorizontalBars items={byPattern} max={100} />
          ) : (
            <p className="muted">No primary pattern breakdown yet.</p>
          )}
        </Card>
        <Card title="Conversion by overall score band" subtitle="Proxy using overall assessment %">
          <HorizontalBars items={byScore} max={100} />
        </Card>
      </div>

      <Card title="Pattern reference" subtitle="Seven canonical bottleneck types tracked in-product">
        <div className="pill-row">
          {PATTERNS.map((p) => (
            <span key={p} className="pill">
              {PATTERN_LABELS[p] || p}
            </span>
          ))}
        </div>
      </Card>
    </div>
  );
}
